'use client';

import { Suspense } from 'react';

import { AnimatePresence } from 'framer-motion';
import { notFound } from 'next/navigation';

import { useUser, useUserPermissions } from 'hooks/auth/user';
import { useStoreQueryEntries } from 'hooks/browser/queryEntries';
import { useGetQueryFilters } from 'hooks/filters';
import {
  useGetCollections,
  useGetCompany,
  useGetStories,
} from 'hooks/pages/companySlug/feed.hooks';
import { usePageTracking } from 'hooks/tracking';

import { getStoryPath } from 'services/storySlug';

import { CollectionsInView } from 'components/collection/CollectionsInView';
import { CollectionsInViewSkeletonGrid } from 'components/collection/CollectionsInViewSkeletonGrid';
import { NewStoryButton } from 'components/creator-toolkit/NewStoryButton';
import { FetchMoreButton } from 'components/shared/layout/FetchMoreButton';
import { TopicFilters } from 'components/shared/layout/TopicFilters';
import { StoryTile } from 'components/story/StoryTile';
import { StoryTileSkeletonGrid } from 'components/story/StoryTileSkeletonGrid';

interface IProps {
  companySlug: string;
}

const FeedViewContent = ({ companySlug }: IProps) => {
  const { company, companyNotFound, companyError } = useGetCompany(companySlug);

  if (companyNotFound) {
    notFound();
  }

  // Surface a failed lookup to app/error.tsx instead of skeletons forever.
  if (companyError) {
    throw new Error(`Failed to load company "${companySlug}"`);
  }

  if (!company) {
    return (
      <>
        <CollectionsInViewSkeletonGrid />

        <div className="mx-auto flex w-full max-w-[1400px] flex-row flex-wrap justify-start sm:px-3">
          <StoryTileSkeletonGrid />
        </div>
      </>
    );
  }

  return <FeedViewCompanyContent company={company} />;
};

const FeedViewCompanyContent = ({
  company,
}: {
  company: NonNullable<ReturnType<typeof useGetCompany>['company']>;
}) => {
  const { canCreateStories } = useUserPermissions(company);
  const { user } = useUser();

  useStoreQueryEntries('FEED');

  const { filtersState } = useGetQueryFilters();

  const { collectionsWithStoryCounts } = useGetCollections(company);
  const hasVisibleCollections = !!collectionsWithStoryCounts?.find(
    collection => collection.publishedStoryCount > 0,
  );

  const { canFetchMore, fetchMore, stories, term } = useGetStories(company);

  return (
    <>
      <TopicFilters />

      {collectionsWithStoryCounts === null ? (
        <CollectionsInViewSkeletonGrid />
      ) : (
        hasVisibleCollections && (
          <CollectionsInView
            company={company}
            collections={collectionsWithStoryCounts}
          ></CollectionsInView>
        )
      )}

      {stories === null ? (
        <div className="mx-auto flex w-full max-w-[1400px] flex-row flex-wrap justify-start sm:px-3">
          <StoryTileSkeletonGrid />
        </div>
      ) : (
        <>
          <p className="mx-auto w-full max-w-[1400px] px-3 pt-4 text-xl font-medium text-darkGrey dark:text-white/80 sm:px-6">
            Single Stories
          </p>

          <div className="mx-auto grid w-full max-w-[1400px] grid-cols-2 sm:px-3 md:grid-cols-3 lg:grid-cols-4 portrait-lg:!grid-cols-3">
            {user &&
              canCreateStories &&
              filtersState?.allTopics?.length === 0 &&
              !term && (
                <div className="p-3 md:p-4">
                  <AnimatePresence>
                    <NewStoryButton variant="large" company={company} user={user} />
                  </AnimatePresence>
                </div>
              )}

            {term && stories.length === 0 && (
              <div className="col-span-full px-4 py-8 text-center md:py-16">
                <div className="pb-2 font-bold">
                  Oh sorry, we couldn&apos;t find any matches for &quot;
                  {term}
                  &quot;.
                </div>

                <div>
                  Please double check for any typos and spelling errors - or try
                  a different search term.
                </div>
              </div>
            )}

            {stories.map((story, index) => {
              const storyUrl = getStoryPath({
                story,
                company,
                path: '/view',
              });
              return (
                <div key={story.id} className="p-3 md:p-4">
                  <StoryTile story={story} storyUrl={storyUrl} preload={index === 0} />
                </div>
              );
            })}

            {!term && (
              <div className="col-span-full">
                <FetchMoreButton fetchMore={fetchMore} canFetchMore={canFetchMore} />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export const FeedView = ({ companySlug }: IProps) => {
  usePageTracking('Company: List');

  return (
    <Suspense>
      <FeedViewContent companySlug={companySlug} />
    </Suspense>
  );
};
